import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { IconButton } from '@understory-io/leaf/icon-button';
import { Text } from '@understory-io/leaf/text';
import { css } from '@understory-io/leaf/css';
import { addDays } from '~/lib/date/add-days';
import { FormattedDate } from '~/ui/formatted';
import { DatePicker } from '~/ui/date-picker';
import { ChevronRightIcon } from '~/ui/icons/chevron-right';

type AvailabilityFilters = {
  languages?: string[];
  locationIds?: string[];
  /** Open-hour bookings only: the booking duration in seconds. A day is only
   *  available for the duration being bought, so the open flow's time step
   *  passes it — otherwise the picker could offer a day with no slots at that
   *  duration. Fixed events ignore it. */
  duration?: number;
  tickets?: { [variantId: string]: number };
};

type DateStepperProps = {
  experienceId: string;
  date: string;
  onDateChange: (date: string) => void;
  filters?: AvailabilityFilters;
  minDate?: string;
  initialFocusDate?: string | null;
};

export type { DateStepperProps, AvailabilityFilters };

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function DateStepper({
  experienceId,
  date,
  onDateChange,
  filters,
  minDate,
  initialFocusDate,
}: DateStepperProps) {
  const intl = useIntl();
  const [pickerOpen, setPickerOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const floor = minDate ?? todayIso();
  const prevDate = addDays(date, -1);
  const nextDate = addDays(date, 1);
  const prevDisabled = prevDate < floor;

  return (
    <div className={rowStyle}>
      <IconButton
        appearance="text"
        size="standard"
        disabled={prevDisabled}
        onClick={() => onDateChange(prevDate)}
        aria-label={intl.formatMessage({
          defaultMessage: 'Previous day',
          description:
            'Accessible label for the date stepper chevron that steps the selected date back one day',
        })}
        className={chevronLeftStyle}
      >
        <ChevronRightIcon />
      </IconButton>

      <button
        ref={triggerRef}
        type="button"
        onClick={() => setPickerOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={pickerOpen}
        className={labelStyle}
      >
        <Text style="body-large-bold" className={dateUnderlineStyle}>
          <FormattedDate date={date} />
        </Text>
      </button>

      <IconButton
        appearance="text"
        size="standard"
        onClick={() => onDateChange(nextDate)}
        aria-label={intl.formatMessage({
          defaultMessage: 'Next day',
          description:
            'Accessible label for the date stepper chevron that steps the selected date forward one day',
        })}
      >
        <ChevronRightIcon />
      </IconButton>

      {pickerOpen && (
        <DatePicker
          experienceId={experienceId}
          selectedDate={date}
          initialFocusDate={initialFocusDate ?? date}
          filters={filters}
          triggerRef={triggerRef}
          onClose={() => setPickerOpen(false)}
          onDateSelect={(d) => {
            onDateChange(d);
            setPickerOpen(false);
          }}
        />
      )}
    </div>
  );
}

const rowStyle = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'small',
});
const chevronLeftStyle = css({ transform: 'rotate(180deg)' });
const labelStyle = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'xsmall',
  background: '[transparent]',
  border: 'none',
  padding: 'none',
  color: 'content.primary',
  cursor: 'interactive',
});
const dateUnderlineStyle = css({
  borderBottom: 'small',
  borderBottomColor: 'content.primary',
});
